import { useState, useEffect, useRef, useMemo } from "react"; 
import {
  TextField,
  Box,
  Divider,
  IconButton,
  Typography,
  Snackbar,
  Alert,
  Grid,
  Paper,
  Autocomplete,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import {
  fetchForInput,
  handleError,
  validateNonEmpty,
  resolveDateFormattingLocale,
  bcp47ToDayjsLocale,
  getDayjsDateTimeFormatForLocale,
  resolveHour12Preference,
} from "../../utils/lib";
import axios from "axios";
import { useSnackbar } from "../../providers/SnackBarProvider";
import { useAppBar } from "../../providers/AppBarProvider";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import 'dayjs/locale/pt';
import 'dayjs/locale/en-gb';
import EditPage from "./EditPage";
import { useConfig } from '../../providers/ConfigProvider';
import { useTranslation } from 'react-i18next';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import DeleteIcon from '@mui/icons-material/Delete';
import PetsIcon from '@mui/icons-material/Pets';
import SearchIcon from '@mui/icons-material/Search';
import { uploadFiles, uploadSingleFile, markFileAsDeleted, prepareFilesForBackend, getProgressMessage } from '../../utils/fileUploadHelper';
import useNavigationHelperStore from '../../store/navigationHelperStore';
import InlinePhotoCapture from "../../components/InlinePhotoCapture";

const EditClinic2 = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const { updateTitle, toggleFab } = useAppBar();
  const {config, isLoadingConfig} = useConfig();
  const { t } = useTranslation();
  const [ isLoading, setIsLoading ] = useState(false);

  const dateFormattingLocale = useMemo(() => resolveDateFormattingLocale(config), [config]);
  const useHour12 = useMemo(
    () => resolveHour12Preference(config, dateFormattingLocale),
    [config, dateFormattingLocale]
  );
  const dateTimePickerFormat = useMemo(
    () => getDayjsDateTimeFormatForLocale(dateFormattingLocale, useHour12),
    [dateFormattingLocale, useHour12]
  );
  const dayjsPickerLocale = useMemo(
    () => bcp47ToDayjsLocale(dateFormattingLocale),
    [dateFormattingLocale]
  );

  const isMetricUnits = useMemo(
    () => (config?.vet?.unit_system ?? 'METRIC') === 'METRIC',
    [config?.vet?.unit_system]
  );
  
  const { updateClinic, pet } = location.state || {};

  const [clinic2, setClinic2] = useState(() => {
    if (updateClinic) {
      let dateValue = updateClinic.date;
      // Always reconstruct as Dayjs, even if it looks like a Dayjs object
      dateValue = dayjs(
        typeof dateValue === 'object' && dateValue !== null && dateValue.$d
          ? dateValue.$d
          : dateValue
      );
      return {
        ...updateClinic,
        date: dateValue,
        pet: pet
      };
    }
    return {
      date: dayjs(),
      visit_reason: "",
      anamnesis: "",
      temp: "",
      weight: "",
      pulse: "",
      respiratory_rate: "",
      inspection: "",
      palpation: "",
      auscultation: "",
      symptoms: [],
      helper_methods: "",
      presumptive_diagnostic: "",
      diagnostic: null,
      treatment: null,
      description: "",
      pet: pet,
    }
  });

  const [errors, setErrors] = useState({});
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const refs = {
    date: useRef(null),
    visit_reason: useRef(null),
    anamnesis: useRef(null),
    temp: useRef(null),
    weight: useRef(null),
    pulse: useRef(null),
    respiratory_rate: useRef(null),
    inspection: useRef(null),
    palpation: useRef(null),
    auscultation: useRef(null),
    symptoms: useRef(null),
    helper_methods: useRef(null),
    presumptive_diagnostic: useRef(null),
    diagnostic: useRef(null),
    treatment: useRef(null),
  };

  const [uploading, setUploading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState({ open: false, text: '', severity: 'info' });
  const fileInputRef = useRef(null);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [showInlineCamera, setShowInlineCamera] = useState(false);
  const { setNavigation, from, state: navState, resetNavigation } = useNavigationHelperStore();

  // Autocomplete options
  const [symptomsOptions, setSymptomsOptions] = useState([]);
  const [diagnosticOptions, setDiagnosticOptions] = useState([]);
  const [treatmentOptions, setTreatmentOptions] = useState([]);

  useEffect(() => {
    setIsLoading(true);

    updateTitle(
      pet?.thumb_url,
      pet?.name,
      clinic2.id ? t('edit.editClinicExtended') : t('edit.newClinicExtended'),
      false,
      PetsIcon
    );

    if (clinic2.resources) {
      setAttachedFiles(clinic2.resources);
    }

    toggleFab(false);
    
    const fetchClinic2ForInput = async () => {
      const clinic2ForInput = await fetchForInput("pets_clinic_2", "pets_clinic_2_for_input");
      //console.log(clinic2ForInput);
      setSymptomsOptions(clinic2ForInput.symptoms);
      setDiagnosticOptions(clinic2ForInput.diagnostics);
      setTreatmentOptions(clinic2ForInput.treatments);

      setIsLoading(false);

      // Set focus on first field (anamnesis) solo si NO estamos volviendo de una búsqueda
      const isReturningFromSearch = location.state?.symptom || location.state?.diagnostic || location.state?.treatment;
      if (!isReturningFromSearch && refs.anamnesis.current) {
        refs.anamnesis.current.focus();
      }
    };
    fetchClinic2ForInput();

  }, []);

  // Restaurar borrador si volvemos desde la cámara o búsquedas
  useEffect(() => {
    if (from === '/main/edit-clinic2' && navState) {
      if (navState.clinic2) {
        setClinic2((prev) => ({
          ...prev,
          ...navState.clinic2,
          date: dayjs(navState.clinic2.date)
        }));
      }
      if (navState.attachedFiles) {
        setAttachedFiles(navState.attachedFiles);
      }
    }
  }, [from, navState]);

  // Handle symptom returned from search
  useEffect(() => {
    const symptom = location.state?.symptom;
    if (symptom) {
      // Agregar síntoma a la lista de síntomas seleccionados
      setClinic2(prev => {
        const currentSymptoms = prev.symptoms || [];
        // No agregar si ya existe
        if (currentSymptoms.find(s => s.id === symptom.id)) {
          return prev;
        }
        return { ...prev, symptoms: [...currentSymptoms, symptom] };
      });
      // Agregar a las opciones si no está presente
      setSymptomsOptions(prev => {
        if (!prev.find(s => s.id === symptom.id)) {
          return [...prev, symptom];
        }
        return prev;
      });
      // Enfocar el campo de síntomas
      setTimeout(() => {
        refs.symptoms.current?.focus();
      }, 100);
    }
  }, [location.state?.symptom]);

  // Handle diagnostic returned from search
  useEffect(() => {
    const diagnostic = location.state?.diagnostic;
    if (diagnostic) {
      setClinic2(prev => ({ ...prev, diagnostic }));
      // Agregar a las opciones si no está presente
      setDiagnosticOptions(prev => {
        if (!prev.find(d => d.id === diagnostic.id)) {
          return [...prev, diagnostic];
        }
        return prev;
      });
      // Enfocar el campo de diagnóstico
      setTimeout(() => {
        refs.diagnostic.current?.focus();
      }, 100);
    }
  }, [location.state?.diagnostic]);

  // Handle treatment returned from search
  useEffect(() => {
    const treatment = location.state?.treatment;
    if (treatment) {
      setClinic2(prev => ({ ...prev, treatment }));
      // Agregar a las opciones si no está presente
      setTreatmentOptions(prev => {
        if (!prev.find(t => t.id === treatment.id)) {
          return [...prev, treatment];
        }
        return prev;
      });
      // Enfocar el campo de tratamiento
      setTimeout(() => {
        refs.treatment.current?.focus();
      }, 100);
    }
  }, [location.state?.treatment]);


  const handleChange = (e) => {
    const { name, value } = e.target;
    setClinic2({ ...clinic2, [name]: value });
  };

  const handleKeyDown = (e, fieldName) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      
      const fieldOrder = [
        'date', 'visit_reason', 'anamnesis', 'temp', 'weight', 
        'pulse', 'respiratory_rate', 'inspection', 'palpation', 
        'auscultation', 'symptoms', 'helper_methods', 
        'presumptive_diagnostic', 'diagnostic', 'treatment'
      ];
      const currentIndex = fieldOrder.indexOf(fieldName);
      
      if (currentIndex >= 0 && currentIndex < fieldOrder.length - 1) {
        // Move to the next field
        const nextField = fieldOrder[currentIndex + 1];
        if (refs[nextField] && refs[nextField].current) {
          refs[nextField].current.focus();
        }
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !validateNonEmpty("anamnesis", clinic2.anamnesis, setErrors, refs.anamnesis)
    )
      return;

      save();
  };

  const save = async () => {
    setIsLoading(true);

    try {
      let response;
      const clinicData = {
        ...clinic2,
        date: dayjs(clinic2.date).format('YYYY-MM-DD HH:mm:ss'),
        resources: prepareFilesForBackend(attachedFiles),
        symptoms: clinic2.symptoms.map(symptom => ({ id: symptom.id, name: symptom.name }))
      };

      //console.log(clinicData);

      if (clinicData.id) {
        response = await axios.put(
          `${import.meta.env.VITE_API_URL}pets_clinic_2?id=${clinicData.id}`,
          clinicData,
          { withCredentials: true }
        );
      } else {
        response = await axios.post(
          `${import.meta.env.VITE_API_URL}pets_clinic_2`,
        clinicData,
        { withCredentials: true }
      );
      }

      //navigate(-1);
      navigate('/main/view-pet', { state: { id: clinic2?.pet?.id, updateLastView: false, ...navState?.state } }, { replace: true });
    } catch (error) {
      handleError(error, snackbar);
    } finally {
      setIsLoading(false);
      resetNavigation();
    }
  };

  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files);
    if (!files.length) return;

    setUploading(true);
    setUploadMessage({
      open: true,
      text: t('common.uploadingFiles'),
      severity: 'info'
    });

    try {
      const result = await uploadFiles({
        files,
        tableName: 'pets_clinic_2',
        type: 'PRIVATE',
        onProgress: (progress, file, index, total) => {
          setUploadMessage({
            open: true,
            text: getProgressMessage(index, total, progress),
            severity: 'info'
          });
        },
        onFileComplete: (uploadedFile) => {
          setAttachedFiles(prev => [...prev, uploadedFile]);
        },
        onError: (error, file) => {
          setUploadMessage({
            open: true,
            text: error,
            severity: 'error'
          });
        }
      });

      if (result.success) {
        setUploadMessage({
          open: true,
          text: t('common.filesUploadedSuccess'),
          severity: 'success'
        });
      } else if (result.successfulUploads > 0) {
        setUploadMessage({
          open: true,
          text: `${result.successfulUploads} / ${result.totalFiles} ${t('common.filesUploadedSuccess')}`,
          severity: 'warning'
        });
      }
    } catch (error) {
      setUploadMessage({
        open: true,
        text: t('common.errorUploadingImages'),
        severity: 'error'
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteFile = async (index) => {
    const fileToDelete = attachedFiles[index];
    const updatedFiles = markFileAsDeleted(attachedFiles, fileToDelete.unique_id);
    setAttachedFiles(updatedFiles);
  };

  const handleUploadClick = () => {
    fileInputRef.current.click();
  };

  const handleCameraClick = () => {
    setShowInlineCamera(true);
  };

  const handleInlinePhotoConfirm = async (file, previewUrl) => {
    setUploading(true);
    setUploadMessage({ open: true, text: t('common.uploadingFiles'), severity: 'info' });
    
    try {
      const uploaded = await uploadSingleFile({
        file: file,
        tableName: 'pets_clinic_2',
        type: 'PRIVATE',
        onProgress: (progress) => {
          setUploadMessage({ open: true, text: getProgressMessage(0, 1, progress), severity: 'info' });
        }
      });
      setAttachedFiles((prev) => [...prev, uploaded]);
      setUploadMessage({ open: true, text: t('common.photoUploadedSuccess'), severity: 'success' });
    } catch (error) {
      setUploadMessage({ open: true, text: error.message || t('common.errorUploadingImages'), severity: 'error' });
    } finally {
      setUploading(false);
    }
  };

  // Guardar estado base para navegación a búsquedas
  const saveStateForNavigation = () => ({
    clinic2: {
      ...clinic2,
      date: dayjs(clinic2.date).toDate(),
    },
    attachedFiles,
    pet
  });

  const handleSearchSymptom = () => {
    setNavigation('/main/edit-clinic2', saveStateForNavigation());
    navigate('/main/search-symptom', { 
      state: { 
        from: '/main/edit-clinic2',
        pet
      } 
    });
  };

  const handleSearchDiagnostic = () => {
    setNavigation('/main/edit-clinic2', saveStateForNavigation());
    navigate('/main/search-diagnostic', { 
      state: { 
        from: '/main/edit-clinic2',
        pet
      } 
    });
  };

  const handleSearchTreatment = () => {
    setNavigation('/main/edit-clinic2', saveStateForNavigation());
    navigate('/main/search-treatment', { 
      state: { 
        from: '/main/edit-clinic2',
        pet
      } 
    });
  };

  return (
    <EditPage 
      onSubmit={handleSubmit}
      isLoading={isLoading || isLoadingConfig}
    >
      <Box sx={{ width: "100%", mt: 1, mb: 2 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={dayjsPickerLocale}>
          <DateTimePicker
            label={t('common.dateTime')}
            value={clinic2.date}
            onChange={(newValue) => setClinic2({ ...clinic2, date: newValue })}
            format={dateTimePickerFormat}
            ampm={useHour12}
            slotProps={{
              textField: {
                fullWidth: true,
                name: "date",
                margin: "normal",
                error: !!errors.date,
                helperText: errors.date,
                inputRef: refs.date,
                onKeyDown: (e) => handleKeyDown(e, "date"),
              },
            }}
          />
        </LocalizationProvider>
      </Box>

      <TextField
        fullWidth
        margin="normal"
        label={t('edit.visitReason')}
        name="visit_reason"
        value={clinic2.visit_reason}
        onChange={handleChange}
        inputRef={refs.visit_reason}
        error={!!errors.visit_reason}
        helperText={errors.visit_reason}
        maxLength={500}
        onKeyDown={(e) => handleKeyDown(e, 'visit_reason')}
      />

      <TextField
        fullWidth
        margin="normal"
        label={t('edit.anamnesis')}
        name="anamnesis"
        value={clinic2.anamnesis}
        onChange={handleChange}
        multiline
        rows={4}
        inputRef={refs.anamnesis}
        error={!!errors.anamnesis}
        helperText={errors.anamnesis}
        maxLength={500}
        onKeyDown={(e) => {
          // Para campos multilinea, solo manejamos Ctrl+Enter para continuar
          if (e.key === 'Enter' && e.ctrlKey) {
            handleKeyDown(e, 'anamnesis');
          }
        }}
      />

      <Box display="flex" gap={2} sx={{ mb: 2 }}>
        <TextField
          fullWidth
          label={t('edit.temperature', { unit: isMetricUnits ? '°C' : '°F' })}
          name="temp"
          value={clinic2.temp}
          onChange={(e) => {
            const value = e.target.value.replace(/[^0-9.,]/g, "");
            setClinic2({ ...clinic2, temp: value });
          }}
          inputProps={{
            inputMode: 'decimal',
            pattern: '[0-9]*[.,]?[0-9]*'
          }}
          inputRef={refs.temp}
          onKeyDown={(e) => handleKeyDown(e, 'temp')}
        />
        <TextField
          fullWidth
          label={t('edit.weightLabel', { unit: isMetricUnits ? 'kg' : 'lb' })}
          name="weight"
          value={clinic2.weight}
          onChange={(e) => {
            const value = e.target.value.replace(/[^0-9.,]/g, "");
            setClinic2({ ...clinic2, weight: value });
          }}
          inputProps={{
            inputMode: 'decimal',
            pattern: '[0-9]*[.,]?[0-9]*'
          }}
          inputRef={refs.weight}
          onKeyDown={(e) => handleKeyDown(e, 'weight')}
        />
      </Box>

      <Box display="flex" gap={2} sx={{ mb: 2 }}>
        <TextField
          fullWidth
          label={t('edit.pulse')}
          name="pulse"
          value={clinic2.pulse}
          onChange={handleChange}
          maxLength={120}
          inputRef={refs.pulse}
          onKeyDown={(e) => handleKeyDown(e, 'pulse')}
        />
        <TextField
          fullWidth
          label={t('edit.respiratoryRate')}
          name="respiratory_rate"
          value={clinic2.respiratory_rate}
          onChange={handleChange}
          maxLength={120}
          inputRef={refs.respiratory_rate}
          onKeyDown={(e) => handleKeyDown(e, 'respiratory_rate')}
        />
      </Box>

      <TextField
        fullWidth
        label={t('edit.inspection')}
        name="inspection"
        value={clinic2.inspection}
        onChange={handleChange}
        maxLength={500}
        sx={{ mb: 2 }}
        inputRef={refs.inspection}
        onKeyDown={(e) => handleKeyDown(e, 'inspection')}
      />

      <TextField
        fullWidth
        label={t('edit.palpation')}
        name="palpation"
        value={clinic2.palpation}
        onChange={handleChange}
        maxLength={500}
        sx={{ mb: 2 }}
        inputRef={refs.palpation}
        onKeyDown={(e) => handleKeyDown(e, 'palpation')}
      />

      <TextField
        fullWidth
        label={t('edit.auscultation')}
        name="auscultation"
        value={clinic2.auscultation}
        onChange={handleChange}
        maxLength={500}
        sx={{ mb: 2 }}
        inputRef={refs.auscultation}
        onKeyDown={(e) => handleKeyDown(e, 'auscultation')}
      />

      <Box display="flex" gap={1} alignItems="flex-start">
        <Autocomplete
          multiple
          fullWidth
          options={symptomsOptions}
          value={clinic2.symptoms}
          onChange={(event, newValue) => {
            setClinic2({ ...clinic2, symptoms: newValue });
          }}
          getOptionLabel={(option) => option?.name || ''}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('edit.symptoms')}
              InputProps={{
                ...params.InputProps,
              }}
              inputRef={refs.symptoms}
              onKeyDown={(e) => handleKeyDown(e, 'symptoms')}
            />
          )}
          freeSolo={false}
          maxLength={120}
          sx={{ mb: 2 }}
        />
        <IconButton onClick={handleSearchSymptom} sx={{ mt: 1 }}>
          <SearchIcon />
        </IconButton>
      </Box>

      <TextField
        fullWidth
        label={t('edit.auxiliaryMethods')}
        name="helper_methods"
        value={clinic2.helper_methods}
        onChange={handleChange}
        maxLength={500}
        sx={{ mb: 2 }}
        inputRef={refs.helper_methods}
        onKeyDown={(e) => handleKeyDown(e, 'helper_methods')}
      />

      <TextField
        fullWidth
        label={t('edit.presumptiveDiagnosis')}
        name="presumptive_diagnostic"
        value={clinic2.presumptive_diagnostic}
        onChange={handleChange}
        maxLength={500}
        sx={{ mb: 2 }}
        inputRef={refs.presumptive_diagnostic}
        onKeyDown={(e) => handleKeyDown(e, 'presumptive_diagnostic')}
      />

      <Box display="flex" gap={1} alignItems="flex-start">
        <Autocomplete
          fullWidth
          options={diagnosticOptions}
          value={clinic2.diagnostic}
          onChange={(event, newValue) => {
            setClinic2({ ...clinic2, diagnostic: newValue });
          }}
          getOptionLabel={(option) => option?.name || ''}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('edit.diagnosis')}
              InputProps={{
                ...params.InputProps,
              }}
              inputRef={refs.diagnostic}
              onKeyDown={(e) => handleKeyDown(e, 'diagnostic')}
            />
          )}
          freeSolo={false}
          maxLength={120}
          sx={{ mb: 2 }}
        />
        <IconButton onClick={handleSearchDiagnostic} sx={{ mt: 1 }}>
          <SearchIcon />
        </IconButton>
      </Box>

      <Box display="flex" gap={1} alignItems="flex-start">
        <Autocomplete
          fullWidth
          options={treatmentOptions}
          value={clinic2.treatment}
          onChange={(event, newValue) => {
            setClinic2({ ...clinic2, treatment: newValue });
          }}
          getOptionLabel={(option) => option?.name || ''}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('common.treatment')}
              InputProps={{
                ...params.InputProps,
              }}
              inputRef={refs.treatment}
              onKeyDown={(e) => handleKeyDown(e, 'treatment')}
            />
          )}
          freeSolo={false}
          maxLength={120}
          sx={{ mb: 2 }}
        />
        <IconButton onClick={handleSearchTreatment} sx={{ mt: 1 }}>
          <SearchIcon />
        </IconButton>
      </Box>

      <Divider sx={{ my: 2 }} />

      <InlinePhotoCapture 
        open={showInlineCamera}
        onPhotoConfirm={handleInlinePhotoConfirm}
        onClose={() => setShowInlineCamera(false)}
      />

      <Box display="flex" gap={4} justifyContent="center">
        <Box display="flex" flexDirection="column" alignItems="center" gap={0.5}>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            multiple
            style={{ display: 'none' }}
          />
          <IconButton onClick={handleUploadClick} disabled={uploading}>
            <AttachFileIcon />
          </IconButton>
          <Typography variant="caption">{t('common.file')}</Typography>
        </Box>

        <Box display="flex" flexDirection="column" alignItems="center" gap={0.5}>
          <IconButton onClick={handleCameraClick} disabled={uploading}>
            <PhotoCameraIcon />
          </IconButton>
          <Typography variant="caption">{t('common.photo')}</Typography>
        </Box>
      </Box>

      {attachedFiles.length > 0 && (
        <>
          <Divider sx={{ my: 2 }} />
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {attachedFiles.filter(file => file.deleted !== 1).map((file, filteredIndex) => {
              // Encontrar el índice original del archivo en el array completo
              const originalIndex = attachedFiles.findIndex(f => f.unique_id === file.unique_id);
              
              return (
                <Grid item xs={4} key={file.unique_id}>
                <Paper 
                  sx={{ 
                    position: 'relative',
                    height: 120,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden'
                  }}
                >
                  {file.type.startsWith('image/') ? (
                    <img 
                      src={file.url} 
                      alt={file.name}
                      style={{ 
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain'
                      }}
                    />
                  ) : (
                    <AttachFileIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                  )}
                  <IconButton
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      backgroundColor: 'rgba(255, 255, 255, 0.8)'
                    }}
                    onClick={() => handleDeleteFile(originalIndex)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Paper>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    display: 'block',
                    textAlign: 'center',
                    mt: 0.5,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {file.name}
                </Typography>
              </Grid>
              );
            })}
          </Grid>
        </>
      )}

      <Snackbar
        open={uploadMessage.open} 
        autoHideDuration={6000}
        onClose={() => setUploadMessage(prev => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setUploadMessage(prev => ({ ...prev, open: false }))} 
          severity={uploadMessage.severity}
          sx={{ width: '100%' }}
        >
          {uploadMessage.text}
        </Alert>
      </Snackbar>

    </EditPage>
  );
};

export default EditClinic2;